<template>
    <div>
      <MessageComposer v-if="maydayChatEnabled"
                       :subscription="subscription"
                       :userActivities="userActivities"
                       :missionInfos="missionInfos" />

      <div class="feed mt-4">
        <div v-for="(userActivity, index) in userActivities" :key="index" class="message">
          <component :is="findComponent(userActivity)"
               v-if="canShowComponent(userActivity)"
               :userActivity="userActivity"
               :subscription="subscription"
               :missionInfos="missionInfos"
               :beneficaryGeolocation="beneficaryGeolocation"
               />
        </div>

        <div v-if="typing" class="message">
          <TypingIndicator />
        </div>
      </div>

    </div>
  </template>

  <script setup>
  import { onMounted, computed } from 'vue'
  import BeneficiaryMessage from "./user_activities_cards/BeneficiaryMessage.vue";
  import TruckMessage from "./user_activities_cards/TruckMessage.vue";
  import TruckGarageOk from "./user_activities_cards/TruckGarageOk.vue";
  import TruckAtGarage from "./user_activities_cards/TruckAtGarage.vue";
  import TruckEnRoute from "./user_activities_cards/TruckEnRoute.vue";
  import TruckOnSite from "./user_activities_cards/TruckOnSite.vue";
  import TruckVehicleInPark from "./user_activities_cards/TruckVehicleInPark.vue";
  import TruckLocation from "./user_activities_cards/TruckLocation.vue";
  import BeneficiaryLocation from "./user_activities_cards/BeneficiaryLocation.vue";
  import GuidedDepollution from "./user_activities_cards/GuidedDepollution.vue";
  import TypingIndicator from "./user_activities_cards/TypingIndicator.vue";

  import MessageComposer from "./MessageComposer.vue";
  import { isNullOrUndefined } from "@lib/utilities"

  const props = defineProps({
    userActivities: {
      type: Array,
      default: () => []
    },
    subscription: {
      type: Object,
      required: true
    },
    maydayChatEnabled: {
      type: Boolean,
      default: () => false
    },
    messagerieMounted: {
      type: Boolean,
      default: () => false
    },
    beneficaryGeolocation: {
      type: Object,
      required: true
    },
    missionInfos: {
      type: Object,
      default: () => {}
    },
    typing: {
      type: Boolean,
      default: () => false
    }
  })

  const emit = defineEmits(['update:messagerieMounted'])

  const truckAlreadyOnSite = computed(() => {
    return props.userActivities.some(userActivity => userActivity?.category === 'truck_on_site')
  })

  const truckAlreadyEnRoute = computed(() => {
    return props.userActivities.some(userActivity => userActivity?.category === 'truck_en_route')
  })

  // The guided depollution "accessibility" question shows a generic tow truck
  // photo, which often differs from the real one shown in the truck_en_route card.
  const isAccessibilityQuestion = (userActivity) => {
    return userActivity.category === 'guided_depollution' && userActivity.data?.step === 'accessibility'
  }

  /**
   * Determines if a user activity component should be displayed.
   * Hides truck geolocation updates when the truck is already on site, and the
   * generic tow truck accessibility question once the real truck is en route.
   * @param {Object} userActivity - The user activity to check
   * @returns {Boolean} - Whether the component should be shown
   */
  const canShowComponent = (userActivity) => {
    if (isNullOrUndefined(userActivity)) {
      return false
    }
    if (userActivity.category === 'truck_geolocation_shared' && truckAlreadyOnSite.value) {
      return false
    }
    if (isAccessibilityQuestion(userActivity) && truckAlreadyEnRoute.value) {
      return false
    }
    return true
  }

  const findComponent = (userActivity) => {
    if (isNullOrUndefined(userActivity)) {
      return;
    }
    const category = userActivity.category;

    const userActivitiesComponents = {
      beneficiary_message: BeneficiaryMessage,
      truck_message: TruckMessage,
      truck_garage_ok: TruckGarageOk,
      truck_at_garage: TruckAtGarage,
      truck_on_site: TruckOnSite,
      truck_en_route: TruckEnRoute,
      truck_vehicle_in_park: TruckVehicleInPark,
      truck_geolocation_shared: TruckLocation,
      beneficiary_geolocation: BeneficiaryLocation,
      guided_depollution: GuidedDepollution
    }

    return userActivitiesComponents[category]
  }

  onMounted(() => {
    emit('update:messagerieMounted', true);
  })
  </script>

  <style scoped>
  /* Clear the fixed composer (and its quick-reply bubbles) so the last message
     is never hidden behind it after scrolling to the bottom. */
  .feed {
    padding-bottom: 150px;
  }
  </style>
